import { isAddable, type ReorderLine } from "@/lib/reorder-diff";

/**
 * Stage-2 (seller-grouped review) domain logic for voice ordering (KAN-143).
 *
 * The confirm endpoint (KAN-147) returns a **flat** `ReorderLine[]` - each line
 * carrying its own resolved `seller` and an available/repriced/unavailable
 * flag - exactly like `/store/stock-tracking/reorder`. The seller-section
 * grouping the review UI shows is a client-side transform over that flat array,
 * not something the backend does; this module owns it so the "N carts" split
 * and the addable/unavailable partition are computed in one tested place.
 */

export interface VoiceSellerGroup {
  seller: { id: string; name: string };
  /** The addable (available + repriced) lines resolved to this seller. */
  lines: ReorderLine[];
}

export interface GroupedConfirmLines {
  /** Addable lines grouped by resolved seller, in first-seen order. */
  sellerGroups: VoiceSellerGroup[];
  /** Lines that cannot be added right now (no live offer), across all sellers. */
  unavailableLines: ReorderLine[];
  /**
   * Distinct suppliers among the addable lines - i.e. how many separate seller
   * carts the order will fan out into. The review copy states this plainly so a
   * one-spoken-order-becomes-N-carts split does not read as a bug.
   */
  sellerCount: number;
}

/**
 * Partition the flat confirm response into per-seller addable groups plus a
 * single unavailable bucket. An addable line always resolves to a seller (it
 * has a live offer); an `unavailable` line carries `seller: null`, so anything
 * not addable - or defensively, addable with no seller - lands in the
 * unavailable bucket rather than a phantom group.
 */
export function groupConfirmLinesBySeller(
  lines: ReorderLine[]
): GroupedConfirmLines {
  const groupBySellerId = new Map<string, VoiceSellerGroup>();
  const orderedSellerIds: string[] = [];
  const unavailableLines: ReorderLine[] = [];

  for (const line of lines) {
    if (isAddable(line) && line.seller) {
      const sellerId = line.seller.id;
      let group = groupBySellerId.get(sellerId);
      if (!group) {
        group = { seller: line.seller, lines: [] };
        groupBySellerId.set(sellerId, group);
        orderedSellerIds.push(sellerId);
      }
      group.lines.push(line);
    } else {
      unavailableLines.push(line);
    }
  }

  const sellerGroups = orderedSellerIds.map(
    (sellerId) => groupBySellerId.get(sellerId)!
  );

  return {
    sellerGroups,
    unavailableLines,
    sellerCount: sellerGroups.length
  };
}
